/**
 * Ticket 5.2: Global Report Button + Modal
 * Placed on all admin/partner pages and driver app.
 * Floating button that opens a report submission modal.
 */
import React, { useState } from 'react';
import { MessageSquareWarning, X, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import config from 'config';

const API_URL = config.API_URL;

const CATEGORIES = [
  { value: 'bug', label: 'Bug / Dysfonctionnement' },
  { value: 'ui_issue', label: "Problème d'affichage" },
  { value: 'performance', label: 'Lenteur / Performance' },
  { value: 'data_error', label: 'Erreur de données' },
  { value: 'feature_request', label: 'Suggestion' },
  { value: 'other', label: 'Autre' },
];

export default function ReportButton({ tokenKey = 'access_token' }) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('bug');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (!description.trim()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem(tokenKey);
      const formData = new FormData();
      formData.append('description', description.trim());
      formData.append('category', category);
      formData.append('page_url', window.location.pathname);
      await axios.post(`${API_URL}/api/reports/submit`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSent(true);
      setTimeout(() => { setOpen(false); setSent(false); setDescription(''); }, 2000);
    } catch {
      alert('Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 z-40 w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/10 transition-all shadow-lg backdrop-blur-sm"
        title="Signaler un problème"
        data-testid="global-report-btn"
      >
        <MessageSquareWarning className="w-4 h-4" />
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center p-4">
          <div className="bg-[#1A1A1A] border border-white/10 w-full max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-4 animate-in slide-in-from-bottom duration-300" data-testid="report-modal">
            {sent ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-green-500/15 flex items-center justify-center mx-auto mb-3">
                  <Send className="w-5 h-5 text-green-400" />
                </div>
                <p className="text-white font-medium">Signalement envoyé</p>
                <p className="text-white/40 text-sm mt-1">Merci pour votre retour</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <MessageSquareWarning className="w-5 h-5 text-[#C9A24D]" /> Signaler un problème
                  </h3>
                  <button onClick={() => setOpen(false)} className="text-white/30 hover:text-white/60"><X className="w-5 h-5" /></button>
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1.5 block">Catégorie</label>
                  <select
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                    className="w-full h-10 px-3 bg-[#0B0B0B] border border-white/10 rounded-lg text-white text-sm"
                    data-testid="report-category"
                  >
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1.5 block">Description</label>
                  <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Décrivez le problème rencontré..."
                    className="w-full p-3 bg-[#0B0B0B] border border-white/10 rounded-lg text-white text-sm h-28 resize-none"
                    data-testid="report-description"
                  />
                </div>
                <p className="text-white/20 text-[10px]">Page: {window.location.pathname} — {new Date().toLocaleString('fr-FR')}</p>
                <div className="flex gap-3">
                  <button onClick={() => setOpen(false)} className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-white/60 text-sm font-medium">Annuler</button>
                  <button
                    onClick={handleSubmit}
                    disabled={loading || !description.trim()}
                    className="flex-1 py-3 rounded-xl bg-[#C9A24D] text-black text-sm font-semibold disabled:opacity-40"
                    data-testid="report-submit-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Envoyer'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
